import { useEffect, useRef, useCallback, useState } from 'react';
import { getOrderItemCount } from '../lib/utils';
import { getWSUrl } from '../services/api';
import { useUIStore } from '../store';

// Generate a notification beep using Web Audio API
const createBeepSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const duration = 0.3;
    const sampleRate = ctx.sampleRate;
    const buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < buffer.length; i++) {
      const t = i / sampleRate;
      const envelope = Math.min(1, Math.min(t * 20, (duration - t) * 20));
      data[i] = envelope * 0.3 * (
        Math.sin(2 * Math.PI * 880 * t) +
        0.5 * Math.sin(2 * Math.PI * 1320 * t)
      );
    }
    return { ctx, buffer };
  } catch (e) {
    return null;
  }
};

const playBeep = (audioCtx) => {
  if (!audioCtx) return;
  try {
    const { ctx, buffer } = audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = 0.5;
    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(0);
  } catch (e) {
    console.error('Beep error:', e);
  }
};

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
};

// Show a browser notification
export const showBrowserNotification = (title, body, onClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'glassorder-' + Date.now(),
      renotify: true,
      requireInteraction: false
    });
    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }
    setTimeout(() => notification.close(), 8000);
  } catch (e) {
    console.error('Notification error:', e);
  }
};

export const useOrdersWebSocket = (establishmentId, onNewOrder, onOrderUpdated) => {
  const wsRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const reconnectTimeoutRef = useRef(null);
  const { soundEnabled, notifPrefs } = useUIStore();
  const audioCtxRef = useRef(null);

  useEffect(() => {
    const initAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = createBeepSound();
      }
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
    document.addEventListener('click', initAudio);
    document.addEventListener('touchstart', initAudio);
    audioCtxRef.current = createBeepSound();
    
    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
    };
  }, []);

  const shouldNotify = useCallback((type, order) => {
    if (type === 'new_order') return notifPrefs.pending;
    if (type === 'order_status_updated') {
      const status = order?.status;
      if (status === 'preparing') return notifPrefs.preparing;
      if (status === 'ready') return notifPrefs.ready;
    }
    return false;
  }, [notifPrefs]);

  const playNotificationSound = useCallback(() => {
    if (soundEnabled) {
      playBeep(audioCtxRef.current);
    }
  }, [soundEnabled]);

  const connect = useCallback(() => {
    if (!establishmentId) return;
    
    const wsUrl = getWSUrl(establishmentId);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setConnectionStatus('connected');
        console.log('WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_order') {
            if (shouldNotify('new_order', data.order)) {
              playNotificationSound();
              showBrowserNotification(
                'Nouvelle commande !',
                `Table ${data.order?.table_number} - ${getOrderItemCount(data.order)} article(s)`
              );
            }
            onNewOrder?.(data.order);
          } else if (data.type === 'order_status_updated') {
            if (shouldNotify('order_status_updated', data.order)) {
              playNotificationSound();
              const statusLabels = { preparing: 'En preparation', ready: 'Prete', delivered: 'Livree' };
              showBrowserNotification(
                `Commande ${statusLabels[data.order?.status] || data.order?.status}`,
                `Table ${data.order?.table_number}`
              );
            }
            onOrderUpdated?.(data.order);
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        setConnectionStatus('reconnecting');
        console.log('WebSocket disconnected, reconnecting...');
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        setConnectionStatus('reconnecting');
        console.error('WebSocket error:', error);
      };
    } catch (e) {
      setConnectionStatus('reconnecting');
      console.error('WebSocket connection error:', e);
      reconnectTimeoutRef.current = setTimeout(connect, 3000);
    }
  }, [establishmentId, onNewOrder, onOrderUpdated, playNotificationSound, shouldNotify]);

  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return connectionStatus;
};
